/**
 * TTS Playback Store (Zustand)
 * Manages text-to-speech playback state for manual (one-shot) playback.
 * Each MessageBubble subscribes via selector to avoid full FlatList re-renders.
 *
 * Error handling: on failure, ttsError shows inline on the TTSButton.
 * Error persists until the user taps the speaker icon again (next speak() clears it).
 */

import { create } from 'zustand';
import * as Speech from 'expo-speech';
import { configureTtsAudioMode, fetchTtsAudio, playTtsClip, type TtsClip, type TtsPlayback, type TtsPlaybackResult } from '../services/serverTtsPlayer';
import { segmentIntoSentences } from '../utils/ttsSegmenter';
import { TTS_REPLAY_PLAYBACK_MAX_MS } from '../config/timeouts';
import { useStreamingTTSStore } from './streamingTTSStore';
import { getActiveCoursePreference, useUserPreferencesStore } from './userPreferencesStore';
import { createLogger } from '../utils/logger';
import { takeAudioFocus, releaseAudioFocus } from '../services/audioFocus';
import { SpeechLogger } from '../modules/AzureSpeechLogger';

const log = createLogger('TTSStore');
const slog = new SpeechLogger(createLogger('TTSStore'), __DEV__, __DEV__);

/** Safe wrapper — Speech.stop() can throw on devices with no TTS engine */
function safeStopSpeech() {
  try { Speech.stop(); } catch { /* no-op */ }
}

/**
 * TTS_TTFA — permanent A/B instrument (server-autoplay-tts.mobile.md
 * ATTS-005): trigger → first-audible per replay tap. PII-free (no text).
 */
function logTtfa(provider: string, triggerAt: number, extra?: Record<string, unknown>) {
  log.info('TTS_TTFA', { provider, ms: Date.now() - triggerAt, surface: 'replay', ...extra });
}

// ── Server TTS playback (azure-server provider) ──────────────────
// Fetch/cache/playback machinery lives in services/serverTtsPlayer (shared
// with the auto-play streaming session).

/** Playback handle for the clip synthesized by POST /api/speech/tts. */
let serverPlayback: TtsPlayback | null = null;

/** Stop and release the server-TTS playback (player + clip) if active. */
function stopServerPlayback() {
  if (serverPlayback) {
    serverPlayback.stop();
    serverPlayback = null;
  }
}

interface TTSError {
  messageId: string;
  i18nKey: string;
}

interface TTSState {
  playingMessageId: string | null;
  ttsError: TTSError | null;

  /** sessionId = the chat session, for cost attribution (ADR-026) — passed
   *  by chat-side callers; this store must not depend on the chat store
   *  (boundaries: tts must not import chat). */
  speak: (messageId: string, text: string, sessionId?: string) => void;
  stop: () => void;
  cleanup: () => void;
}

type TTSSet = (partial: Partial<TTSState>) => void;
type TTSGet = () => TTSState;

function setTTSError(messageId: string, i18nKey: string, set: TTSSet) {
  set({ playingMessageId: null, ttsError: { messageId, i18nKey } });
}

/** Speak using device native TTS (expo-speech) */
async function speakWithDevice(messageId: string, text: string, set: TTSSet, get: TTSGet, triggerAt: number) {
  try {
    // Fast check: are there any voices available?
    const voices = await Speech.getAvailableVoicesAsync();
    if (get().playingMessageId !== messageId) return;
    if (voices.length === 0) {
      throw new Error('No TTS voices available');
    }

    await new Promise<void>((resolve, reject) => {
      const { targetLocale } = useUserPreferencesStore.getState();
      let started = false;
      Speech.speak(text, {
        language: targetLocale,
        rate: 0.9,
        onStart: () => {
          if (!started) {
            started = true;
            logTtfa('device', triggerAt, { chars: text.length });
          }
        },
        onDone: () => resolve(),
        onError: () => reject(new Error('Device TTS failed')),
        onStopped: () => resolve(),
      });
    });
    if (get().playingMessageId === messageId) {
      set({ playingMessageId: null });
    }
  } catch (error: unknown) {
    if (get().playingMessageId !== messageId) return;
    slog.rnAnomaly('DeviceTTSFailed', { error });
    setTTSError(messageId, 'chat.tts.playbackError', set);
  }
}

/**
 * Speak via the backend proxy (`POST /api/speech/tts`, server-speech-proxy
 * Phase 1) — the default and sole Azure TTS path. The server resolves/enforces
 * the voice (standard-Neural policy) and meters ground-truth cost at the
 * synthesis seam (no client-side usage report).
 *
 * The endpoint is a POST, so the player can't be pointed at the URL for
 * progressive playback (player sources are GET-only): the bytes are fetched
 * through the authed axios client, written to a cache file (blob URL on web),
 * and played with expo-audio. Today's backend provider emits the whole clip
 * in one chunk anyway, so nothing is lost versus progressive playback.
 * Falls back to device TTS (expo-speech) on any error.
 */
async function speakWithServer(messageId: string, text: string, set: TTSSet, get: TTSGet, triggerAt: number, sessionId?: string) {
  const pref = getActiveCoursePreference();
  // Send the user's course voice so the A/B compares the same voice; the
  // server still enforces the standard-Neural policy.
  const voice = pref?.azureTtsVoice;

  // Chunk the reply into sentences (prosody-preserving — see
  // segmentIntoSentences) and play them sequentially with prefetch depth 1:
  // the next sentence synthesizes while the current one plays, so a long reply
  // starts on sentence 1 instead of waiting for the whole clip. A short,
  // single-sentence reply is one chunk = the previous behavior.
  const chunks = segmentIntoSentences(text);
  const superseded = () => get().playingMessageId !== messageId;

  // Text still to speak if we bail to device TTS — advanced past each chunk
  // that finishes, so a mid-stream failure resumes on device without
  // re-speaking (or overlapping) what Azure already voiced.
  let remainingText = text;

  // Fetches never reject: they resolve to {clip} or {error}, so a prefetch
  // that fails while the current chunk is still playing can't surface as an
  // unhandled rejection — it's inspected when we await it. fetchMs is the
  // wall-clock of the POST round-trip (backend synth + transfer) for the
  // chunk — the TTS_SEGMENT A/B instrument.
  const fetchChunk = (
    i: number,
    signal: AbortSignal,
  ): Promise<{ clip?: TtsClip; error?: unknown; fetchMs: number }> => {
    const fetchStartedAt = Date.now();
    return fetchTtsAudio(chunks[i], { voice, messageId, sessionId, signal }).then(
      (clip) => ({ clip, fetchMs: Date.now() - fetchStartedAt }),
      (error) => ({ error, fetchMs: Date.now() - fetchStartedAt }),
    );
  };

  try {
    // Start fetching sentence 0 immediately; configure the audio mode
    // concurrently (both overlap, shaving first-audio latency). A stop during
    // this window is caught by the loop's first supersede check, which
    // releases the in-flight clip.
    let prefetchController = new AbortController();
    let nextFetch = fetchChunk(0, prefetchController.signal);
    await configureTtsAudioMode();
    let firstLogged = false;

    for (let i = 0; i < chunks.length; i++) {
      const fetched = await nextFetch;
      if (superseded()) { fetched.clip?.release(); return; }
      if (!fetched.clip) throw fetched.error ?? new Error('server_fetch_failed');
      const clip = fetched.clip;

      // Prefetch the next chunk (if any) before playing this one.
      prefetchController = new AbortController();
      const prefetch = i + 1 < chunks.length ? fetchChunk(i + 1, prefetchController.signal) : null;
      nextFetch = prefetch ?? Promise.resolve({ fetchMs: 0 });
      const dropPrefetch = () => {
        prefetchController.abort();
        void prefetch?.then((n) => n.clip?.release());
      };

      // playerInitMs: wall-clock of the synchronous playTtsClip() (player
      // construction + first play() dispatch). True first-audible isn't
      // observable across the expo-audio bridge — play() returns before audio
      // starts — so this is a lower bound on device decoder/player init.
      const playStartedAt = Date.now();
      const playback = playTtsClip(clip);
      const playerInitMs = Date.now() - playStartedAt;
      serverPlayback = playback;
      // Per-segment latency decomposition for the server-path A/B (PII-free —
      // never the chunk text). messageId is threaded to POST /speech/tts.
      log.info('TTS_SEGMENT', {
        provider: 'azure-server',
        surface: 'replay',
        messageId,
        segmentIndex: i,
        chars: chunks[i].length,
        fetchMs: fetched.fetchMs,
        playerInitMs,
      });
      if (!firstLogged) {
        firstLogged = true;
        logTtfa('azure-server', triggerAt, { chars: text.length });
      }

      // Bounded wait: expo-audio's finish event can silently never fire (the
      // failure class the auto-play watchdog recovers from), and an unbounded
      // await here left the speaker button stuck with no error and no
      // fallback. On the guard firing, stop the wedged player and route
      // through the shared error path (device TTS speaks the remainder).
      let stallTimer: ReturnType<typeof setTimeout> | undefined;
      const result = await Promise.race([
        playback.done,
        new Promise<TtsPlaybackResult>((resolve) => {
          stallTimer = setTimeout(
            () => resolve({ finished: false, error: 'playback_stalled' }),
            TTS_REPLAY_PLAYBACK_MAX_MS,
          );
        }),
      ]);
      clearTimeout(stallTimer);
      if (result.error === 'playback_stalled') {
        slog.rnAnomaly('ServerTTSPlaybackStalled', { segmentIndex: i });
        playback.stop();
      }
      if (serverPlayback === playback) serverPlayback = null;

      if (superseded()) { dropPrefetch(); return; }
      if (result.error != null) {
        slog.rnAnomaly('ServerTTSPlaybackFailed', { error: result.error });
        dropPrefetch();
        throw new Error('server_playback_failed');
      }

      // Chunk done — it's no longer part of the device-fallback remainder.
      remainingText = chunks.slice(i + 1).join(' ');
    }

    if (!superseded()) set({ playingMessageId: null });
    return;
  } catch (error: unknown) {
    if (superseded()) return;

    stopServerPlayback();
    slog.rnAnomaly('ServerTTSFailed', { error });

    if (!remainingText) {
      if (get().playingMessageId === messageId) set({ playingMessageId: null });
      return;
    }

    // Fallback to device native TTS for the REMAINING text only (the whole
    // text if nothing has played yet) — no overlap with Azure's voice.
    try {
      slog.rnEvent('DeviceTTSFallback', 'start');
      await new Promise<void>((resolve, reject) => {
        const { targetLocale: fallbackLocale } = useUserPreferencesStore.getState();
        let started = false;
        Speech.speak(remainingText, {
          language: fallbackLocale,
          rate: 0.9,
          onStart: () => {
            if (!started) {
              started = true;
              logTtfa('device', triggerAt, { fallback: true, chars: remainingText.length });
            }
          },
          onDone: () => resolve(),
          onError: () => reject(new Error('Device TTS failed')),
          onStopped: () => resolve(),
        });
      });
      if (get().playingMessageId === messageId) {
        set({ playingMessageId: null });
      }
      return;
    } catch {
      slog.rnAnomaly('DeviceTTSFallbackFailed');
    }

    setTTSError(messageId, 'chat.tts.playbackError', set);
  }
}

export const useTTSStore = create<TTSState>((set, get) => ({
  playingMessageId: null,
  ttsError: null,

  speak: (messageId: string, text: string, sessionId?: string) => {
    const triggerAt = Date.now();
    const { playingMessageId } = get();

    // Any user action clears the error
    set({ ttsError: null });

    // Cancel streaming TTS if active (user chose manual playback)
    useStreamingTTSStore.getState().cancel();

    // Toggle off if same message
    if (playingMessageId === messageId) {
      safeStopSpeech();
      stopServerPlayback();
      set({ playingMessageId: null });
      releaseAudioFocus('chat-tts');
      return;
    }

    // Stop current playback
    safeStopSpeech();
    stopServerPlayback();

    set({ playingMessageId: messageId });
    // Claim audio focus — pauses the Audio Lessons player if it held it (one
    // output at a time). Yields back by stopping this one-shot TTS.
    takeAudioFocus('chat-tts', () => get().stop());

    // 'device' → expo-speech; everything else → the server-synthesis path
    // (POST /api/speech/tts), the default and sole Azure path.
    const coursePref = getActiveCoursePreference();
    if (coursePref?.ttsProvider === 'device') {
      speakWithDevice(messageId, text, set, get, triggerAt);
    } else {
      speakWithServer(messageId, text, set, get, triggerAt, sessionId);
    }
  },

  stop: () => {
    safeStopSpeech();
    stopServerPlayback();
    set({ playingMessageId: null, ttsError: null });
    releaseAudioFocus('chat-tts');
  },

  cleanup: () => {
    safeStopSpeech();
    stopServerPlayback();
    set({ playingMessageId: null, ttsError: null });
    releaseAudioFocus('chat-tts');
  },
}));
